"use client";

import type React from "react";
import type { DefaultBackgroundProps } from "../types";

const getSizeClasses = (size: "sm" | "md" | "lg") => {
  switch (size) {
    case "sm":
      return "w-3 h-3";
    case "md":
      return "w-6 h-6";
    case "lg":
      return "w-8 h-8";
    default:
      return "w-6 h-6";
  }
};

const DefaultBackground: React.FC<DefaultBackgroundProps> = ({
  children,
  decorativeElements,
  className = "",
  positionMode = "absolute",
  fullHeight = true,
}) => {
  const heightClass = fullHeight ? "min-h-screen" : "";

  return (
    <div className={`${heightClass} bg-[#f8f6f0] relative ${className}`}>
      {/* Decorative Elements */}
      {decorativeElements.map((element, _index) => {
        const positionStyles: React.CSSProperties = {};

        if (element.position.top) positionStyles.top = element.position.top;
        if (element.position.bottom)
          positionStyles.bottom = element.position.bottom;
        if (element.position.left) positionStyles.left = element.position.left;
        if (element.position.right)
          positionStyles.right = element.position.right;

        // Create a unique key based on element properties
        const uniqueKey = `${element.size}-${element.position.top || ""}-${element.position.bottom || ""}-${element.position.left || ""}-${element.position.right || ""}-${element.opacity}`;

        const positionClass = positionMode === "fixed" ? "fixed" : "absolute";

        return (
          <div
            key={uniqueKey}
            className={`${positionClass} ${getSizeClasses(element.size)} bg-black rounded-full pointer-events-none`}
            style={{
              ...positionStyles,
              opacity: element.opacity,
            }}
          />
        );
      })}

      {/* Content */}
      {children}
    </div>
  );
};

export default DefaultBackground;

// Instructions to use the DefaultBackground component:
// 1. Import the component: import { DefaultBackground } from '@/ui';
// 2. Create an array of decorative elements:
//    const decorativeElements: DecorativeElement[] = [
//      { size: 'md', position: { top: '10%', right: '8%' }, opacity: 0.12 },
//      { size: 'sm', position: { bottom: '15%', left: '5%' }, opacity: 0.10 },
//      { size: 'lg', position: { top: '60%', right: '15%' }, opacity: 0.08 }
//    ];
// 3. Use the component in your layout:
//    <DefaultBackground decorativeElements={decorativeElements}>
//      <YourContent />
//    </DefaultBackground>
